import type { ChartArtifactPayload } from '@cotera/api';

export type SparkBarBucket = {
  count: number;
  start: number;
  end: number;
};

/** Normalized viewBox for all spark SVGs. */
export const SPARK_VIEW_WIDTH = 100;
export const SPARK_VIEW_HEIGHT = 40;

/** Values per category label — sums all datasets for stacked charts. */
export function chartPayloadToSparkValues(
  payload: ChartArtifactPayload
): number[] {
  const { labels, datasets } = payload.data;
  if (datasets.length === 0) {
    return labels.map(() => 0);
  }
  return labels.map((_label, index) =>
    datasets.reduce((sum, dataset) => sum + (dataset.values[index] ?? 0), 0)
  );
}

export function chartPayloadToSparkBuckets(
  payload: ChartArtifactPayload
): SparkBarBucket[] {
  const values = chartPayloadToSparkValues(payload);
  return values.map((count, index) => ({
    count,
    start: index,
    end: index + 1,
  }));
}

export function isBarLikeChartType(
  chartType: ChartArtifactPayload['chart_type']
): boolean {
  return (
    chartType === 'bar' || chartType === 'stacked_bar' || chartType === 'pie'
  );
}

export function isLineLikeChartType(
  chartType: ChartArtifactPayload['chart_type']
): boolean {
  return (
    chartType === 'line' || chartType === 'area' || chartType === 'scatter'
  );
}

/** Normalized [x,y] points in 0–1 space for SVG sparklines. */
export function chartPayloadToSparkPoints(
  payload: ChartArtifactPayload
): Array<{ x: number; y: number }> {
  const dataset = payload.data.datasets[0];
  if (dataset === undefined) {
    return [];
  }

  const values =
    payload.chart_type === 'scatter'
      ? payload.data.labels.map((_label, index) => dataset.values[index] ?? 0)
      : dataset.values;

  if (values.length === 0) {
    return [];
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min === 0 ? 1 : max - min;
  const lastIndex = Math.max(values.length - 1, 1);

  return values.map((value, index) => ({
    x: index / lastIndex,
    y: 1 - (value - min) / range,
  }));
}

export function sparkPointsToPolyline(
  points: Array<{ x: number; y: number }>,
  width: number = SPARK_VIEW_WIDTH,
  height: number = SPARK_VIEW_HEIGHT
): string {
  return points
    .map((point) => {
      const x = point.x * width;
      const y = point.y * height;
      return `${x},${y}`;
    })
    .join(' ');
}

export function sparkPointsToAreaPath(
  points: Array<{ x: number; y: number }>,
  width: number = SPARK_VIEW_WIDTH,
  height: number = SPARK_VIEW_HEIGHT
): string {
  if (points.length === 0) {
    return '';
  }
  const line = points
    .map((point, index) => {
      const x = point.x * width;
      const y = point.y * height;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
  const lastX = points[points.length - 1]?.x ?? 0;
  const firstX = points[0]?.x ?? 0;
  return `${line} L ${lastX * width} ${height} L ${firstX * width} ${height} Z`;
}
